import { Feedback } from "../pages/health/encounters/cards/vitals/types/commonCDSTypes";

export type WaistSex = "female" | "male";

/** IDF/WHO Caucasian waist thresholds (cm). */
export const waistRanges = {
  female: { increased: 80, high: 88 },
  male: { increased: 94, high: 102 },
} as const;

export function resolveWaistSex(gender?: string | null): WaistSex | undefined {
  if (!gender) return undefined;
  const normalized = gender.toLowerCase();
  if (normalized === "female" || normalized === "transgender-female") {
    return "female";
  }
  if (normalized === "male" || normalized === "transgender-male") {
    return "male";
  }
  return undefined;
}

export function calculateWaistFeedback(
  waistCm: number,
  gender?: string | null,
): Feedback {
  if (Number.isNaN(waistCm) || waistCm <= 0) {
    return { message: "Invalid waist value", type: "error" };
  }

  const sex = resolveWaistSex(gender);
  if (!sex) {
    return {
      message: "Waist risk thresholds require patient sex",
      type: "info",
    };
  }

  const { increased, high } = waistRanges[sex];
  const sexLabel = sex === "female" ? "female" : "male";

  if (waistCm >= high) {
    return {
      message: `High cardiometabolic risk (≥${high} cm for ${sexLabel}) - Medical evaluation recommended`,
      type: "error",
    };
  }

  if (waistCm >= increased) {
    return {
      message: `Increased cardiometabolic risk (≥${increased} cm for ${sexLabel}) - Consider lifestyle modifications`,
      type: "warning",
    };
  }

  return { message: `Normal waist range for ${sexLabel}`, type: "success" };
}

export function waistCheckDisplay(
  waistCm: number,
  gender?: string | null,
): { classification: string; displayColour: string } {
  if (Number.isNaN(waistCm) || waistCm <= 0) {
    return { classification: "", displayColour: "black" };
  }

  const sex = resolveWaistSex(gender);
  if (!sex) {
    return { classification: "", displayColour: "black" };
  }

  const { increased, high } = waistRanges[sex];
  if (waistCm >= high) {
    return { classification: "High risk", displayColour: "red" };
  }
  if (waistCm >= increased) {
    return { classification: "Increased risk", displayColour: "orange" };
  }
  return { classification: "Normal", displayColour: "gray" };
}
